import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getCatalogBrands, type CatalogBrandSummary } from "@/lib/api-client";
import { LoadingBlock } from "@/components/product/QueryFeedback";

/** Regroupe les marques triées par première lettre (accents/casse ignorés pour le tri et le regroupement). */
function groupByFirstLetter(brands: CatalogBrandSummary[]): Map<string, CatalogBrandSummary[]> {
  const groups = new Map<string, CatalogBrandSummary[]>();
  for (const brand of brands) {
    const letter = brand.name.charAt(0).toLocaleUpperCase("fr-FR").normalize("NFD").replace(/[̀-ͯ]/g, "");
    const key = /[A-Z]/.test(letter) ? letter : "#";
    const list = groups.get(key) ?? [];
    list.push(brand);
    groups.set(key, list);
  }
  return groups;
}

/**
 * Équivalent de apps/web/app/(shop)/marques/page.tsx. Comme l'original
 * (`try {} catch { brands = [] }`), une erreur réseau retombe sur la liste
 * vide plutôt que de faire échouer toute la page — même message « répertoire
 * indisponible » que la version d'origine.
 */
export function MarquesPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["catalog", "brands"],
    queryFn: getCatalogBrands,
  });

  if (isLoading) return <LoadingBlock />;

  const brands = isError ? [] : data ?? [];
  const sorted = [...brands].sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }));
  const groups = groupByFirstLetter(sorted);
  const letters = [...groups.keys()].sort((a, b) => a.localeCompare(b, "fr"));

  return (
    <div className="container-vvs py-8">
      <header className="mb-8">
        <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">Toutes les marques</h1>
        <p className="mt-2 font-body text-sm text-muted">Retrouvez l&apos;ensemble des marques disponibles sur VVS.</p>
      </header>

      {letters.length === 0 ? (
        <p className="py-16 text-center font-body text-base text-ink">
          Le répertoire des marques n&apos;est pas disponible pour le moment.
        </p>
      ) : (
        <>
          {/* Ancres rapides */}
          <nav aria-label="Navigation alphabétique" className="mb-8 flex flex-wrap gap-2 border-b border-line pb-6">
            {letters.map((letter) => (
              <a
                key={letter}
                href={`#lettre-${letter}`}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-line font-body text-sm text-ink hover:border-navy hover:text-navy"
              >
                {letter}
              </a>
            ))}
          </nav>

          <div className="space-y-10">
            {letters.map((letter) => (
              <section key={letter} id={`lettre-${letter}`} aria-labelledby={`titre-${letter}`}>
                <h2 id={`titre-${letter}`} className="mb-3 font-display text-lg font-bold text-golddark">
                  {letter}
                </h2>
                <ul className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 md:grid-cols-4">
                  {groups.get(letter)!.map((brand) => (
                    <li key={brand.id}>
                      <Link
                        to={`/marques/${brand.slug}`}
                        className="flex items-center gap-2.5 font-body text-sm text-ink hover:text-navy"
                      >
                        {brand.logoUrl ? (
                          <img
                            src={brand.logoUrl}
                            alt=""
                            loading="lazy"
                            className="h-9 w-9 shrink-0 rounded-full object-cover"
                          />
                        ) : (
                          <span
                            aria-hidden="true"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-navy font-display text-xs font-semibold text-cream"
                          >
                            {brand.name.charAt(0)}
                          </span>
                        )}
                        <span>
                          {brand.name}
                          {brand.countryOfOrigin ? (
                            <span className="block text-xs text-muted">{brand.countryOfOrigin}</span>
                          ) : null}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
